import {
  Box,
  Divider,
  Flex,
  Button,
  Icon,
  Heading,
  Spinner,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { RiAddLine, RiSearchLine } from 'react-icons/ri';
import { MdUpdate } from 'react-icons/md';
import { useEffect, useMemo } from 'react';
import { Header } from '../../components/Header';
import ReactTableComponent from '../../components/TableComponent';
import api from '../../service/api';
import { getColumn } from '../../utils/getProductColumn';
import { useHistory } from 'react-router-dom';
import { SiderbarResponsive } from '../../components/SiderbarResponsive';
import { Wapper } from '../../components/Wapper';
import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ExcludeDialog } from '../../components/ExlcudeDialog';
import apiIntegrador from '../../service/apiIntegrador';
import { ehLogistica } from '../../helpers/perfilLogistica';

// Remove acentos e caixa pra comparação de busca (ex.: "cateter" encontra "CATÉTER").
function normalizaBusca(v?: string): string {
  return (v ?? '')
    .toString()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

export default function ListProduct() {
  const [products, setProducts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [idToDelete, setIdToDelete] = useState('');
  const [search, setSearch] = useState('');

  const filteredProducts = useMemo(() => {
    const termo = normalizaBusca(search);
    if (!termo) return products;

    return (products as any[]).filter((produto) => {
      return (
        normalizaBusca(produto?.nome).includes(termo) ||
        normalizaBusca(produto?.codigo).includes(termo) ||
        normalizaBusca(produto?.categorias?.nome).includes(termo)
      );
    });
  }, [products, search]);

  function handleDelete(id: any) {
    setIdToDelete(id);
    onOpen();
  }

  const { isOpen, onOpen, onClose } = useDisclosure();

  const toast = useToast();

  const { user } = useAuth();
  const logistica = ehLogistica(user);
  const column = getColumn(handleDelete, '/edit/produto', !logistica);
  const history = useHistory();

  // Uma etapa da sincronização com o ERP: avisa o resultado e, se falhar, não
  // interrompe as etapas seguintes.
  const sincronizarCadastro = async (rota: string, titulo: string) => {
    try {
      setIsLoading(true);
      const resposta = await apiIntegrador.get(rota);
      toast({
        title: titulo,
        description: `${resposta.data?.message}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: 'Erro na atualização',
        description: error?.response?.data?.message || 'Tente mais tarde',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const updateProducts = async () => {
    setIsLoading(true);
    toast({
      title: 'Sincronização iniciada',
      description: 'Essa operação de levar alguns minutos',
      status: 'warning',
      duration: 3000,
      isClosable: true,
    });

    try {
      const responseCategorias = await apiIntegrador.get(
        '/get-categorias-from-erp'
      );

      if (responseCategorias.status === 200) {
        toast({
          title: 'Categorias atualizadas',
          description: `${responseCategorias.data?.message}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      if (error.response.data.message) {
        toast({
          title: 'Erro na atualização',
          description: `${error.response.data.message}`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        setIsLoading(false);
      } else {
        toast({
          title: 'Erro na atualização',
          description: `Tente mais tarde`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        setIsLoading(false);
      }
    }

    try {
      setIsLoading(true);
      const responseProdutos = await apiIntegrador.get('/get-produto-from-erp');

      if (responseProdutos.status === 200) {
        toast({
          title: 'Produtos atualizados',
          description: `${responseProdutos.data?.message}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      if (error.response.data.message) {
        toast({
          title: 'Erro na atualização',
          description: `${error.response.data.message}`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Erro na atualização',
          description: `Tente mais tarde`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
      setIsLoading(false);
    }

    try {
      setIsLoading(true);
      const responseTabelaPreco = await apiIntegrador.get(
        '/get-tabelas-preco-from-erp'
      );

      if (responseTabelaPreco.status === 200) {
        toast({
          title: 'Tabelas preço atualizadas',
          description: `${responseTabelaPreco.data?.message}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Erro na atualização',
          description: `Tente mais tarde`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      if (error.response.data.message) {
        toast({
          title: 'Erro na atualização',
          description: `${error.response.data.message}`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Erro na atualização',
          description: `Tente mais tarde`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
      setIsLoading(false);
    }

    try {
      setIsLoading(true);
      const responseColaboradores = await apiIntegrador.get(
        '/get-colaborador-from-erp'
      );

      if (responseColaboradores.status === 200) {
        toast({
          title: 'Colaboradores atualizadas',
          description: `${responseColaboradores.data?.message}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Erro na atualização',
          description: `Tente mais tarde`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      if (error.response.data.message) {
        toast({
          title: 'Erro na atualização',
          description: `${error.response.data.message}`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Erro na atualização',
          description: `Tente mais tarde`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
      setIsLoading(false);
    }

    try {
      setIsLoading(true);
      const responseClientes = await apiIntegrador.get('/get-cliente-from-erp');

      if (responseClientes.status === 200) {
        toast({
          title: 'Clientes atualizados',
          description: `${responseClientes.data?.message}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Erro na atualização',
          description: `Tente mais tarde`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      if (error.response.data.message) {
        toast({
          title: 'Erro na atualização',
          description: `${error.response.data.message}`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Erro na atualização',
          description: `Tente mais tarde`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
      setIsLoading(false);
    }
    try {
      setIsLoading(true);
      const responseItensTabelaPrecoProduto = await apiIntegrador.get(
        '/get-tabela-preco-produto-from-erp'
      );

      if (responseItensTabelaPrecoProduto.status === 200) {
        toast({
          title: 'Clientes atualizados',
          description: `${responseItensTabelaPrecoProduto.data?.message}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setIsLoading(false);
      } else {
        toast({
          title: 'Erro na atualização',
          description: `Tente mais tarde`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      if (error.response.data.message) {
        toast({
          title: 'Erro na atualização',
          description: `${error.response.data.message}`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Erro na atualização',
          description: `Tente mais tarde`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
      setIsLoading(false);
    }

    // Médicos e convênios também vêm do ERP e alimentam o agendamento e o
    // apontamento. Sem estas etapas, um médico cadastrado no TOTVS só aparecia
    // depois da rotina automática da API (10h, 15h e 23h45).
    await sincronizarCadastro('/get-medicos-from-erp', 'Médicos atualizados');
    await sincronizarCadastro('/get-convenios-from-erp', 'Convênios atualizados');
    // Condições de pagamento: mesma situação — a lista do pedido no app é a
    // cópia do cadastro do TOTVS, e uma condição nova só chegava pela rotina.
    await sincronizarCadastro(
      '/get-cpagamento-from-erp',
      'Condições de pagamento atualizadas'
    );
    setIsLoading(false);
  };

  const x = async (e: any, id: string) => {
    try {
      await api.patch(`/api-essencial/v1/produtos/update-excluido/${id}`, {
        excluido: true,
      });

      const response = await api.get(
        // `/api-essencial/v1/produtos/${user.empresa.id}/empresa?excluido=false`
        `/api-essencial/v1/produtos`
      );

      setProducts(response.data);

      toast({
        title: 'Categoria deletada com sucesso',
        description: ``,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      history.push('/listar/categoria');
    } catch (error) {
      if (products.length === 1) {
        toast({
          title: 'Categoria deletada com sucesso',
          description: ``,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setProducts([]);
      }
    }
  };

  useEffect(() => {
    const getData = async () => {
      try {
        const dados = (await api.get(`/api-essencial/v1/produtos`)).data;

        const vistos = new Set<string>();
        setProducts((Array.isArray(dados) ? dados : []).filter((produto: any) => {
          const id = produto?.produto_id || produto?.id;
          if (!id || vistos.has(id)) return false;
          vistos.add(id);
          return true;
        }));
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    getData();
  }, [user.empresa.id]);

  return (
    <>
      <Header />
      <SiderbarResponsive />

      <Flex align="start" mx="auto" mt="8" px="6">
        <Wapper>
          <Box flex="1" p="8" bg="gray.800" borderRadius={8} mb="16">
            <Flex justify="space-between" align="center">
              <Heading size="md" fontWeight="normal">
                LISTA DE PRODUTOS
              </Heading>
              <Button
                as="a"
                size="sm"
                fontSize="sm"
                disabled={isLoading}
                bg="green.500"
                color="white"
                _hover={{
                  bg: 'green.700',
                }}
                leftIcon={<Icon as={MdUpdate} />}
                cursor="pointer"
                onClick={updateProducts}
              >
                Sincronizar
              </Button>
              {/* <Button
                as="a"
                size="sm"
                fontSize="sm"
                colorScheme="orange"
                leftIcon={<Icon as={RiAddLine} />}
                cursor="pointer"
                onClick={() => history.push("/cadastro/produto")}
              >
                Cadastrar
              </Button> */}
            </Flex>

            <Divider my="6" borderColor="gray.700" />

            <InputGroup mb="6" maxW="420px">
              <InputLeftElement pointerEvents="none">
                <Icon as={RiSearchLine} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Buscar por nome, código ou categoria"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>

            <Flex justifyContent="center">
              {isLoading ? (
                <Spinner color="white" />
              ) : (
                <>
                  {products.length === 0 ? (
                    <Flex>
                      <Text color="orange.200">Sem produtos para exibir</Text>
                    </Flex>
                  ) : filteredProducts.length === 0 ? (
                    <Flex>
                      <Text color="orange.200">
                        Nenhum produto encontrado para essa busca
                      </Text>
                    </Flex>
                  ) : (
                    <ReactTableComponent
                      columns={column}
                      data={filteredProducts}
                      isPagenable
                    />
                  )}
                </>
              )}
            </Flex>
          </Box>
        </Wapper>
      </Flex>

      <ExcludeDialog
        isOpen={isOpen}
        onClose={onClose}
        label="categoria"
        deleteFunction={(e) => {
          x(e, idToDelete);
          onClose();
        }}
      />
    </>
  );
}
